import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/utils/Button";
import { useForm } from "react-hook-form";
import moment from "moment";
import Calendar2LineIcon from "remixicon-react/Calendar2LineIcon";
import DatePicker from "react-datepicker";
import CardLayout from "../../../components/utils/CardLayout";
import SearchBar from "../../../components/utils/SearchBar";
import Table from "../../../components/utils/Table";
import networkActions from "../../../redux/network/actions";
import shortCodeActions from "../../../redux/shortCodeData/actions";
import DownloadCloud2sLineIcon from "remixicon-react/DownloadCloud2LineIcon";
import mdnSwapReport from "../../../redux/MDNSwapReport/actions";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import { convertDateTimeToRequiredFormatTZ } from "../../../helpers/commonFunction";
import Tag from "../../../components/utils/Tag";

const { getActiveNetwork } = networkActions;
const { getShortCode } = shortCodeActions;
const { getMdnSwapReportRequestList } = mdnSwapReport;

function MDNSwapReport() {
  const dispatch = useDispatch();
  const datePickerRef = useRef(null);
  const datePickerRef2 = useRef(null);
  const [filterText, setFilterText] = useState("");
  const [toDate, setToDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getTime() - 2 * 24 * 60 * 60 * 1000),
  );

  const { networkResult } = useSelector((state) => state.network);
  const { mdnSwapReportResult, loading } = useSelector(
    (state) => state.mdnSwapReport,
  );

  const { register, handleSubmit, getValues } = useForm({
    defaultValues: {
      networkID: "",
      MSISDN: "",
      statusID: "",
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
    },
  });

  const searchValues = getValues();
  const searchData = useMemo(() => {
    return {
      ...searchValues,
      toDate: moment(toDate).format("YYYY-MM-DD"),
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
    };
  }, [searchValues, toDate, fromDate]);

  const {
    register: registerFilter,
    setValue: setValueFilter,
  } = useForm({ defaultValues: searchData });

  const { shortCodeResult } = useSelector((state) => state.shortCode);

  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  useEffect(() => {
    dispatch(getActiveNetwork());
    dispatch(getMdnSwapReportRequestList(searchData));
    dispatch(
      getShortCode({
        tableName: "activationrequest",
        fieldName: "RequestStatus",
      }),
    );
  }, [dispatch]);

  const handleFilterChange = (value) => {
    setFilterText(value);
  };

  const onSubmit = (data) => {
    const searchRequest = {
      ...data,
      toDate: moment(toDate).format("YYYY-MM-DD"),
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
    };
    dispatch(getMdnSwapReportRequestList(searchRequest));
  };

  const openDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  const openDatePicker2 = () => {
    if (datePickerRef2.current) {
      datePickerRef2.current.setOpen(true);
    }
  };

  useEffect(() => {
    if (networkResult?.length > 0) {
      const network = networkResult.find(
        (item) => item.IsActive == 1 && item.ID == 4,
      );

      if (network) {
        setValueFilter("networkID", String(network.ID));
      }
    }
  }, [networkResult, setValueFilter]);

  const columns = [
    { displayName: "#", key: "key" },
    { displayName: "BULK TRANSACTION ID", key: "bulkTransactionID" },
    { displayName: "TRANSACTION ID", key: "transactionID" },
    { displayName: "SERIAL NUMBER", key: "serialNumber" },
    { displayName: "MSISDN", key: "MSISDN" },
    { displayName: "NEW MSISDN", key: "newMSISDN" },
    { displayName: "AREA CODE", key: "areaCode" },
    { displayName: "NETWORK", key: "network" },
    { displayName: "DEALER", key: "dealer" },
    { displayName: "DATE", key: "date" },
    { displayName: "STATUS", key: "status" },
    { displayName: "REMARKS", key: "remarks" },
  ];

  const filteredItems = Array.isArray(mdnSwapReportResult)
    ? mdnSwapReportResult.filter((item) =>
        Object.values(item).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(filterText.toLowerCase()),
        ),
      )
    : [];

  const dataSource = filteredItems.map((item, key) => ({
    key: key + 1,
    bulkTransactionID: item.BatchTransactionId,
    transactionID: item.TransactionID,
    serialNumber: item.SerialNumber,
    MSISDN: item.MSISDN,
    newMSISDN: item.NewMSISDN,
    areaCode: item.Zipcode,
    network: item.NetworkName,
    dealer: item.DealerName,
    date:
      item.CreatedDtTm !== null
        ? convertDateTimeToRequiredFormatTZ(item.CreatedDtTm)
        : "",
    status: (
      <Tag
        color={
          item.RequestStatus === 13
            ? "badge-danger"
            : item.RequestStatus === 11
              ? "badge-warning"
              : item.RequestStatus === 12
                ? "badge-success"
                : item.RequestStatus === 32
                  ? "badge-secondary"
                  : "badge-secondary"
        }
        title={item.RequestStatusDesc}
      />
    ),
    remarks: item.Remark,
  }));
  const listDataModel =
    mdnSwapReportResult?.map((item, key) => ({
      "Sr. No": key + 1,
      BulkTransactionID: item.BatchTransactionId,
      TransactionID: item.TransactionID,
      SerialNumber: item.SerialNumber,
      MSISDN: item.MSISDN,
      NewMSISDN: item.NewMSISDN,
      AreaCode: item.Zipcode,
      Network: item.NetworkName,
      Dealer: item.DealerName,
      Dealer: item.DealerName,
      Date:
        item.CreatedDtTm !== null
          ? convertDateTimeToRequiredFormatTZ(item.CreatedDtTm)
          : "",
      Status: item.RequestStatusDesc,
      Remark: item.Remark,
    })) || [];

  const exportToCSVModal = async () => {
    if (listDataModel && listDataModel.length > 0) {
      const ws = XLSX.utils.json_to_sheet(listDataModel);
      const wb = {
        Sheets: { mdnSwapReportResult: ws },
        SheetNames: ["mdnSwapReportResult"],
      };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      await FileSaver.saveAs(data, "MDN_Swap_Report" + fileExtension);
    }
  };

  return (
    <>
      <CardLayout title="MDN Swap Report">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-row">
            <div className="col-lg-2 col-md-4 mb-2">
              <label>Select Network</label>

              <select
                className="form-control"
                id="networkID"
                {...registerFilter("networkID")}
              >
                {networkResult
                  ?.filter((item) => item.IsActive == 1 && item.ID == 4)
                  .map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.NetworkName}
                    </option>
                  ))}
              </select>
            </div>

            <div className="col-lg-2 col-md-4 mb-2">
              <label>Select Status</label>
              <select className="form-control" {...register("statusID")}>
                <option value="">Select</option>

                {shortCodeResult
                  ?.filter(
                    (item) =>
                      item.IsActive == 1 &&
                      [10, 11, 12, 13, 32].includes(Number(item.ID)),
                  )
                  .map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.Value}
                    </option>
                  ))}
              </select>
            </div>

            <div className="col-lg-2 col-md-4 mb-2 d-flex flex-column">
              <label>From Date</label>

              <Calendar2LineIcon
                onClick={openDatePicker}
                size="1.5rem"
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  top: "36px",
                  right: "12px",
                  zIndex: 1,
                }}
              />

              <DatePicker
                ref={datePickerRef}
                selected={fromDate}
                className="form-control"
                onChange={(date) => setFromDate(date)}
                dateFormat="yyyy-MM-dd"
                showMonthDropdown
                showYearDropdown
                onKeyDown={(e) => e.preventDefault()}
              />
            </div>

            <div className="col-lg-2 col-md-4 mb-2 d-flex flex-column">
              <label>To Date</label>

              <Calendar2LineIcon
                onClick={openDatePicker2}
                size="1.5rem"
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  top: "36px",
                  right: "12px",
                  zIndex: 1,
                }}
              />

              <DatePicker
                ref={datePickerRef2}
                selected={toDate}
                className="form-control"
                onChange={(date) => setToDate(date)}
                dateFormat="yyyy-MM-dd"
                showMonthDropdown
                showYearDropdown
                onKeyDown={(e) => e.preventDefault()}
              />
            </div>

            <div className="col-lg-2 mb-2">
              <label>MSISDN</label>

              <input
                type="text"
                className="form-control"
                placeholder="Enter MSISDN"
                {...register("MSISDN")}
              />
            </div>
            <div className="mt-4 ml-4" id="main-btn-cdnq" m>
              <Button
                title="Search"
                style="minWidth:80px"
                buttonClass={`btn btn-primary mt-1 btn-md Activity_searchBtn__GlBtC`}
                buttonType="submit"
              />
            </div>
          </div>
        </form>
        <hr />

        <div className="row mb-3">
          <div className="col-lg-3  activation-request-search-btn mb-sm-444">
            <SearchBar onFilter={handleFilterChange} filterText={filterText} />
          </div>
        </div>

        <div className="d-flex justify-content-end mb-3">
          {dataSource && dataSource.length > 0 && (
            <Button
              onClick={exportToCSVModal}
              title="Export"
              icon={<DownloadCloud2sLineIcon size={15} />}
              style={{ minWidth: "120px" }}
              buttonClass="btn acti-export-btn-v acti-ex-btn font-weight-bold text-white"
            />
          )}
        </div>

        <div id="text-center">
          <Table
            columns={columns}
            loading={loading}
            data={dataSource}
            pagination={true}
          />
          ;
        </div>
      </CardLayout>
    </>
  );
}

export default MDNSwapReport;
